import { common as c } from '@pendo/aggregations';
import moment, { DATE_FORMAT } from '@utils/moment-wrapper';
import { i18n_t as $t } from '@utils/i18n';
import { EngageApplication } from '@/types/engage.types';

const DEFAULT_GROUP_SIZE = 50;
const MIN_GROUP_SIZE = 0;
const MAX_GROUP_SIZE = 100;

export type ExperimentState = 'draft' | 'active' | 'needsReview' | 'completed';

export type ExperimentCompletionBehavior = 'disable' | 'keepPublic' | 'promoteWinner';

export interface Guide {
    id: string;
    name: string;
    appId?: number; // appId is optional for guideB
}

export interface ExperimentVariant {
    name?: string;
    guideId: string;
    groupSize: number;
}

export interface ExperimentStatusOption {
    label: string;
    value: ExperimentState;
}

interface ExperimentConversion {
    itemType?: 'Page' | 'Feature' | 'TrackType';
    itemId?: string;
    attributionWindow: number;
}

interface ExperimentAttributes {
    attributionWindowTiming: 'DAY' | 'HOUR';
    durationTiming: 'DAY' | 'HOUR' | 'WEEK' | 'MONTH';
    durationNumber: number;
}

interface AudienceUiHint {
    filters: Array<{
        kind: string;
        segmentId: string;
    }>;
}

interface ExperimentRollout {
    currentPercentage: number;
}

interface AutoPromoteConfig {
    audience?: c.PipelineData[];
    audienceUiHint?: AudienceUiHint;
    rollout?: ExperimentRollout
}

export interface Experiment {
    id?: string;
    appId?: number;
    name: string;
    description: string;
    state: ExperimentState;
    audienceUiHint?: AudienceUiHint;
    audience?: c.PipelineData[];
    rollout?: ExperimentRollout;
    variants: ExperimentVariant[];
    conversion: ExperimentConversion;
    startTime: number;
    endTime: number;
    attributes: ExperimentAttributes;
    completionBehavior: ExperimentCompletionBehavior;
    autoPromoteConfig: AutoPromoteConfig;
}

export type ExperimentApp = Pick<EngageApplication, 'id' | 'applicationFlags'>;

export const experimentStatusDisplayNames: Record<ExperimentState, string> = {
    draft: 'Draft',
    active: 'Active',
    needsReview: 'Ready for review',
    completed: 'Completed',
};

export const experimentStatusOptions: ExperimentStatusOption[] = [
    { label: experimentStatusDisplayNames.draft, value: 'draft' },
    { label: experimentStatusDisplayNames.active, value: 'active' },
    { label: experimentStatusDisplayNames.needsReview, value: 'needsReview' },
    { label: experimentStatusDisplayNames.completed, value: 'completed' },
];

export const promoteWinnerDisabledReason = $t('guides.experiments.settings.completionBehavior.promoteWinner_disableTooltip');

export const getCompletionBehaviorOptions = (hasConversionMetric: boolean) => [
    {
        label: $t('guides.experiments.settings.completionBehavior.disable_label'),
        id: 'disable',
        description: $t('guides.experiments.settings.completionBehavior.disable_description') },
    {
        label: $t('guides.experiments.settings.completionBehavior.keepPublic_label'),
        id: 'keepPublic',
        description: $t('guides.experiments.settings.completionBehavior.keepPublic_description'),
    },
    {
        label: $t('guides.experiments.settings.completionBehavior.promoteWinner_label'),
        id: 'promoteWinner',
        description: $t('guides.experiments.settings.completionBehavior.promoteWinner_description'),
        disabled: !hasConversionMetric,
        tooltip: hasConversionMetric ? null : {
            content: promoteWinnerDisabledReason,
            multiline: true,
        },
    },
];

const GUIDE_ICON = {
    type: 'guide',
    fill: '#009855',
    stroke: '#ffffff',
    style: { 'backgroundColor': '#009855', 'padding': '4px', 'border-radius': '3px' },
};
const I18N_TIMING_KEYS = {
    HOUR: 'activateModalScheduleTextInHours',
    DAY: 'activateModalScheduleTextInDays',
    WEEK: 'activateModalScheduleTextInWeeks',
    MONTH: 'activateModalScheduleTextInMonths',
};

const BEAKER_ICON = {
    type: 'beaker',
    fill: '#006632',
    stroke: '#ffffff',
    style: { 'backgroundColor': '#006632', 'padding': '4px', 'border-radius': '3px' },
};

export const addGuideToExperimentOptions = {
    SELECT_EXISTING_GUIDE: {
        id: 'select',
        label: $t('guides.experiments.addGuideToExperiment.Select an existing guide'),
        icon: GUIDE_ICON,
    },
    CLONE_EXISTING_GUIDE: {
        id: 'clone',
        label: $t('guides.experiments.addGuideToExperiment.Clone from existing guide'),
        icon: GUIDE_ICON,
    },
    CREATE_NEW_GUIDE: {
        id: 'create',
        label: $t('guides.experiments.addGuideToExperiment.Create new guide'),
        icon: GUIDE_ICON,
    },
    ADD_CONTROL_GROUP: {
        id: 'control',
        label: $t('guides.experiments.Add a "No guide" group'),
        icon: BEAKER_ICON,
    },
};

export const formatExperimentDuration = (experiment: Experiment): string | null => {
    if (!experiment?.attributes?.durationTiming || !experiment?.attributes?.durationNumber || !experiment?.endTime) {
        return null;
    }
    if (!I18N_TIMING_KEYS[experiment.attributes.durationTiming]) {
        return null;
    }

    const { durationTiming, durationNumber } = experiment.attributes;
    const key = I18N_TIMING_KEYS[durationTiming];
    const endDate = moment(experiment.endTime).format(DATE_FORMAT.full);

    return $t(key, { count: durationNumber, endDate }) as string;
};

export const createDefaultExperiment = (options: Partial<Experiment> = {}): Experiment => {
    const defaults: Experiment = {
        name: $t('guides.experiments.defaultName', {
            timestamp: moment().format('M/D [@] h:mm A'),
        }),
        description: '',
        state: 'draft',
        variants: [],
        conversion: {
            attributionWindow: 10_800_000, // 3 hours
        },
        startTime: 0,
        endTime: 0,
        attributes: {
            attributionWindowTiming: 'HOUR',
            durationTiming: 'WEEK',
            durationNumber: 2,
        },
        completionBehavior: 'keepPublic',
        autoPromoteConfig: {},
    };

    return {
        ...defaults,
        ...options,
    };
};

export function createVariantUpdate (guideId: string, variantPosition: 'A' | 'B', currentVariants: ExperimentVariant[]) {
    const variants: ExperimentVariant[] = [...currentVariants];
    const newVariant: ExperimentVariant = {
        guideId,
        groupSize: DEFAULT_GROUP_SIZE,
    };

    if (variantPosition === 'B' && variants.length === 0) {
        throw new Error('Cannot add Variant B without first adding Variant A');
    }

    // If this is replacing an existing variant, use its group size
    // If this is the first variant, use the defaults
    // If this is the second variant, use whatever distribution remains
    if (variantPosition === 'A') {
        variants[0] = variants[0] ? { ...variants[0], guideId } : newVariant;
    } else if (variantPosition === 'B') {
        if (variants[1]) {
            variants[1] = { ...variants[1], guideId };
        } else {
            // Use the remaining distribution while ensuring it's within bounds.
            const groupSize = Math.max(
                MIN_GROUP_SIZE,
                Math.min(MAX_GROUP_SIZE, MAX_GROUP_SIZE - (variants[0].groupSize || 0)),
            );

            variants[1] = {
                ...newVariant,
                groupSize,
            };
        }
    }

    return variants;
}

export const experimentStatusColorMap = {
    active: 'green',
    draft: 'blue',
    needsReview: 'teal',
    completed: 'purple',
    unknown: 'gray',
};

export function calculateProcessingEndTime (experiment: Experiment): number {
    const { endTime, conversion } = experiment;
    const attributionWindow = conversion?.attributionWindow || 0;
    const attributionEnd = endTime + attributionWindow;

    // Round the end of the attribution window to the next hour plus an additional 15 minutes to
    // ensure that the hourly rollup job has completed.
    return moment(attributionEnd)
        .startOf('hour')
        .add(1, 'hour')
        .add(15, 'minutes')
        .valueOf();
}

export function isExperimentProcessing (experiment: Experiment): boolean {
    const { state, endTime } = experiment;
    const now = Date.now();
    // We additionaly check for "active" here to catch scenarios where the backend job
    // hasn't moved completed experiments into the completed state yet.
    const statesForProcessing = ['active','needsReview', 'completed'];

    if (!statesForProcessing.includes(state)) return false;
    if (now <= endTime) return false;

    return now < calculateProcessingEndTime(experiment);
}

export function isControlGroup (variant: ExperimentVariant | undefined) {
    return variant?.guideId === '';
}

export function hasControlGroup (variants: ExperimentVariant[]) {
    return variants?.some(isControlGroup);
}

export function validateVariantUpdate (
    status: ExperimentState,
    variants: ExperimentVariant[],
    guidesMap: Record<string, Guide>,
) {
    let appId = null;

    // Validate that all variants have the same app ID.
    for (const variant of variants) {
        const guideAppId = guidesMap[variant.guideId]?.appId;
        if (appId == null) {
            appId = guideAppId;
        }

        if (!!guideAppId && appId !== guideAppId) throw new Error('All variants must have the same app ID');
    }

    // Validate that the current experiment status allows for variant updates.
    if (status !== 'draft') throw new Error('Experiment must be in draft state to update variants');

    return appId;
}

export const WILL_RUN_FOR_KEYS = {
    DAY: 'guides.experiments.metrics.willRunForDays',
    WEEK: 'guides.experiments.metrics.willRunForWeeks',
    MONTH: 'guides.experiments.metrics.willRunForMonths',
};
